/**
 * dashboard.service.ts
 *
 * Typed API call wrapper for the dashboard domain.
 * Unwraps the Axios response and returns the typed payload directly.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import type { PatientListParams } from '@/types/patient.types';

// ─── Types (mirrored from server dashboard.service.ts) ────────────────────────

export interface StageCount {
  stageId: string | number;
  stageName: string;
  count: number;
}

export interface PlanListItem {
  planId: number;
  patientId: number;
  patientName: string | null;
  stageId: number | null;
  stageName: string | null;
  lastContact: string | null;
  totalQuote: number | null;
  displayId: string | null;
}

export interface UpcomingAppt {
  appointmentId: number;
  patientName: string;
  startTime: string;
  endTime: string;
  appointmentTypeName: string;
}

export interface DashboardSummary {
  plansByStage: StageCount[];
  tasksDueToday: number;
  tasksPastDue: number;
  unreadMessages: number;
  todayAppointments: number;
  recentPlans: PlanListItem[];
  upcomingAppointments: UpcomingAppt[];
}

export interface FollowedPlan {
  planId: number;
  patientName: string | null;
  displayId: string | null;
  stageName: string | null;
}

export interface FollowedPlansResponse {
  data: FollowedPlan[];
  total: number;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export function getSummary(params: Omit<PatientListParams, 'page' | 'limit'> = {}): Promise<DashboardSummary> {
  return apiClient
    .get<ApiResponse<DashboardSummary>>('/dashboard/summary', { params })
    .then((r) => r.data.data);
}

/**
 * Fetch plans the current user is following.
 * Falls back to an empty list if the endpoint is not yet implemented.
 */
export function getFollowedPlans(): Promise<FollowedPlansResponse> {
  return apiClient
    .get<ApiResponse<FollowedPlansResponse>>('/dashboard/following')
    .then((r) => r.data.data)
    .catch(() => ({ data: [], total: 0 }));
}

// ─── Common Links ──────────────────────────────────────────────────────────────

export interface CommonLink {
  id: number;
  title: string;
  description: string;
  url: string;
  image: string;
  order: number;
}

export function getCommonLinks(): Promise<CommonLink[]> {
  return apiClient
    .get<ApiResponse<CommonLink[]>>('/dashboard/common-links')
    .then((r) => r.data.data)
    .catch(() => []);
}
